import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { useAuth } from '../context/AuthContext';
import './Dashboard.css';

const AgencyDetails = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { currentUser, isSuperAdmin } = useAuth();
  const [agency, setAgency] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [adminUsers, setAdminUsers] = useState([]);

  // Fetch agency details
  useEffect(() => {
    const fetchAgencyDetails = async () => {
      try {
        setLoading(true);
        
        // Create a public API instance
        const publicApiInstance = axios.create({
          baseURL: `${window.location.protocol}//${window.location.hostname}${window.location.port ? `:${window.location.port}` : ''}/api/public`,
          headers: { 'Content-Type': 'application/json' }
        });
        
        // Add auth token if available
        const token = localStorage.getItem('token');
        if (token) {
          publicApiInstance.defaults.headers.common['Authorization'] = `Bearer ${token}`;
        }
        
        // Fetch agency data
        const [agencyResponse, usersResponse] = await Promise.all([
          publicApiInstance.get(`/agencies/${id}`),
          publicApiInstance.get('/users')
        ]);
        
        if (!agencyResponse.data) {
          throw new Error('Agency not found');
        }
        
        setAgency(agencyResponse.data);
        
        // Filter agent admin users for this agency
        if (usersResponse.data && Array.isArray(usersResponse.data)) {
          const agentAdmins = usersResponse.data.filter(
            user => user.role === 'agent_admin' && user.agencyId === id
          );
          setAdminUsers(agentAdmins);
        }
        
        setLoading(false);
      } catch (err) {
        console.error('Error fetching agency details:', err);
        setError('Failed to load agency details. Please try again later.');
        setLoading(false);
      }
    };
    
    // Only fetch if we have an ID and the user is authorized
    if (id && isSuperAdmin()) {
      fetchAgencyDetails();
    } else if (!isSuperAdmin()) {
      setError('Access denied. Admin privileges required.');
      setLoading(false);
    }
  }, [id, isSuperAdmin]);

  const handleBackClick = () => {
    navigate('/admin-dashboard');
  };

  if (loading) {
    return (
      <div className="agency-details-page">
        <Header />
        <div className="container">
          <div className="loading">Loading agency details...</div>
        </div>
        <Footer />
      </div>
    );
  }

  if (error) {
    return (
      <div className="agency-details-page">
        <Header />
        <div className="container">
          <div className="error-message">{error}</div>
          <button className="btn-primary" onClick={handleBackClick}>
            Back to Dashboard
          </button>
        </div>
        <Footer />
      </div>
    );
  }

  return (
    <div className="agency-details-page">
      <Header />
      <div className="container">
        <div className="page-header">
          <h1>Agency Details</h1>
          <button className="btn-primary" onClick={handleBackClick}>
            Back to Dashboard
          </button>
        </div>
        
        {agency && (
          <div className="agency-details-content">
            <div className="agency-info card">
              <h2>{agency.name}</h2>
              <div className="info-row">
                <label>Email:</label>
                <p>{agency.email || agency.contactEmail || 'N/A'}</p>
              </div>
              <div className="info-row">
                <label>Phone:</label>
                <p>{agency.phone || agency.contactNumber || 'N/A'}</p>
              </div>
              <div className="info-row">
                <label>Location:</label>
                <p>{agency.location || agency.address || 'N/A'}</p>
              </div>
              {agency.description && (
                <div className="info-row">
                  <label>Description:</label>
                  <p>{agency.description}</p>
                </div>
              )}
              <div className="info-row">
                <label>Status:</label>
                <p className={agency.isActive ? 'status-active' : 'status-inactive'}>
                  {agency.isActive ? 'Active' : 'Inactive'}
                </p>
              </div>
            </div>
            
            <div className="agent-admins card">
              <h3>Agency Administrators ({adminUsers.length})</h3>
              {adminUsers.length === 0 ? (
                <p>No administrators assigned to this agency.</p>
              ) : (
                <table className="admin-table">
                  <thead>
                    <tr>
                      <th>Name</th>
                      <th>Email</th>
                      <th>Phone</th>
                    </tr>
                  </thead>
                  <tbody>
                    {adminUsers.map(admin => (
                      <tr key={admin._id}>
                        <td>{admin.name}</td>
                        <td>{admin.email}</td>
                        <td>{admin.phone || 'N/A'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          </div>
        )}
      </div>
      <Footer />
    </div>
  );
};

export default AgencyDetails; 